/**
 * Кольца активности.
 *
 * У Apple кольцо всегда означает «набрать»: оно заполняется, когда день идёт
 * хорошо. Поэтому калории сюда не попали — их цель обратная, не превысить, и
 * заполненное кольцо означало бы провал. Калорийный бюджет живёт отдельным
 * числом, а в кольцах то, что действительно набирают: белок, тренировка, шаги.
 */

export interface Ring {
  id: string
  label: string
  value: number
  target: number
  unit: string
  color: string
  /** Данных нет вовсе (часы не подключены) — кольцо остаётся контуром. */
  missing?: boolean
}

export function ActivityRings({ rings, size = 148 }: { rings: Ring[]; size?: number }) {
  const center = size / 2

  // Толщина и зазор считаются от размера: на фиксированных 15 пикселях три
  // кольца при 148 пикселях сливаются в сплошное пятно, а смысл кольца в том,
  // что каждое читается отдельно.
  const RING_WIDTH = size * 0.088
  const GAP = size * 0.032

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Кольца дня">
      {rings.map((ring, index) => {
        const radius = center - RING_WIDTH / 2 - index * (RING_WIDTH + GAP)
        const circumference = 2 * Math.PI * radius
        const rawShare = ring.target > 0 ? ring.value / ring.target : 0
        const share = Math.min(1, rawShare)
        const filled = ring.missing ? 0 : circumference * share

        // Перевыполнение уходит на второй круг, как на часах: цель по белку
        // берётся регулярно, и без второго круга награда за это никак не
        // выражена — кольцо просто упирается в 100%.
        const overflow = ring.missing ? 0 : Math.min(1, Math.max(0, rawShare - 1))

        return (
          <g key={ring.id} transform={`rotate(-90 ${center} ${center})`}>
            {/* Подложка — тот же цвет в четверть силы, как на часах. */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={ring.color}
              strokeOpacity={ring.missing ? 0.18 : 0.25}
              strokeWidth={RING_WIDTH}
            />
            {filled > 0 && (
              /* Штрих длиной в полную окружность, а видимую часть задаёт
                 смещение: только так дуга растёт от 12 часов. При длине
                 штриха меньше периода анимация смещения гоняет дугу ПО кольцу
                 вместо роста. */
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={ring.color}
                strokeWidth={RING_WIDTH}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference - filled}
                style={{
                  ['--ring-empty' as string]: `${circumference}`,
                  animation: 'ring-draw 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
            )}

            {overflow > 0 && (
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={ring.color}
                strokeWidth={RING_WIDTH * 0.55}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference - circumference * overflow}
                opacity={0.9}
                style={{ filter: 'brightness(1.35)' }}
              />
            )}
          </g>
        )
      })}
    </svg>
  )
}

/** Легенда справа от колец: название, набрано из цели, единица. */
export function RingLegend({ rings }: { rings: Ring[] }) {
  return (
    <div className="flex flex-1 flex-col justify-center gap-3">
      {rings.map((ring) => (
        <div key={ring.id}>
          <div className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: ring.color }}>
            {ring.label}
          </div>
          {ring.missing ? (
            <div className="text-[15px] text-ink-2">нет данных</div>
          ) : (
            <div className="numerals flex items-baseline gap-1">
              <span className="text-[22px] font-semibold">{Math.round(ring.value)}</span>
              <span className="text-[15px] text-ink-2">
                /{Math.round(ring.target)} {ring.unit}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
